import { getActivePinia } from 'pinia'
import { toValue } from 'vue'

import { permissionAndSettingsStore } from '@/entities/settings-permission-and-services'

import { useCurrentUser } from '@/shared/composables/current-user'
import { useUserData, UserFields } from '@/shared/composables/local-storage'
import { UserRoles } from '@/shared/types/router'
import { FeaturesNames } from '@/shared/types/settings'
import { isStageOrDevelopment } from '@/shared/utils/import-meta'

import { useFeatureAccessEmulatorStore } from './feature-access-emulator'

const isSupportRole = (): boolean => {
  try {
    return useUserData().getField(UserFields.USER_ROLE) === UserRoles.SUPPORT
  } catch {
    return false
  }
}

const withFeatureAccessEmulator = <T>(
  resolve: (store: ReturnType<typeof useFeatureAccessEmulatorStore>) => T,
  fallback: T,
): T => {
  if (!isStageOrDevelopment() || !getActivePinia()) {
    return fallback
  }
  return resolve(useFeatureAccessEmulatorStore())
}

/**
 * Безопасно резолвит доступ: при отсутствии Pinia/моков (тесты, ранняя инициализация)
 * не блокируем UI — fail-open.
 */
const safeResolve = (resolve: () => boolean): boolean => {
  try {
    return resolve()
  } catch {
    return true
  }
}

/** Веб-клиент (звонки, конференции, контакты, календарь, уведомления). */
export const resolveWebClientUiEnabled = (): boolean => safeResolve(() => {
  if (isSupportRole()) return true
  if (!getActivePinia()) return true

  const currentUserResult = useCurrentUser()
  const currentUser = currentUserResult?.currentUser
  if (!currentUser?.value) return true

  const apiValue = Boolean(currentUser.value.webSoftphoneEnabled)
  return withFeatureAccessEmulator(
    (emulator) => emulator.resolveWebSoftphone(apiValue),
    apiValue,
  )
})

/** Feature-группа InstantMessaging с учётом dev-эмулятора. */
export const resolveInstantMessagingUiEnabled = (): boolean => safeResolve(() => {
  if (isSupportRole()) return true
  if (!getActivePinia()) return true

  const store = permissionAndSettingsStore.useStoreSettingsServicesAndPackages()
  const apiValue = toValue(store.getEnabledFeatures).has(FeaturesNames.INSTANT_MESSAGING)
  return withFeatureAccessEmulator(
    (emulator) => emulator.resolveInstantMessaging(apiValue),
    apiValue,
  )
})

/** Полный UC: веб-клиент + InstantMessaging — чаты и переходы в /chat. */
export const resolveChatMessagingUiEnabled = (): boolean =>
  resolveWebClientUiEnabled() && resolveInstantMessagingUiEnabled()

/**
 * Ждёт первую загрузку услуг/ACL. До этого InstantMessaging в пустом Set выглядит как «выключен».
 * Если Pinia ещё нет — сразу resolve (fail-open), как и остальные резолверы доступа.
 */
export const whenAccessFeaturesReady = async (): Promise<void> => {
  if (!getActivePinia()) return
  try {
    await permissionAndSettingsStore.useStoreSettingsServicesAndPackages().whenAccessFeaturesReady()
  } catch {
    return
  }
}

/**
 * Услуга «Детализация вызовов» (CDRsAccess).
 * Без неё — без выгрузки истории и с укороченным окном в списке вызовов (WUI-4375).
 */
export const resolveCallDetailingEnabled = (): boolean => safeResolve(() => {
  if (isSupportRole()) return true
  if (!getActivePinia()) return true

  const store = permissionAndSettingsStore.useStoreSettingsServicesAndPackages()
  const apiValue = toValue(store.getEnabledFeatures).has(FeaturesNames.CDRS_ACCESS)
  return withFeatureAccessEmulator(
    (emulator) => emulator.resolveCdrsAccess(apiValue),
    apiValue,
  )
})

/** Глубина списка истории вызовов (дни), если CDRsAccess выключен. */
export const CALL_HISTORY_LIMITED_DAYS = 2
